import React from "react";
import { Building, CheckCircle2, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";

interface AddBrandStepReviewProps {
  isDetailsFetched: boolean;
  brandName: string;
  onBrandNameChange: (value: string) => void;
  logoUrl: string;
  onLogoError: () => void;
}

export const AddBrandStepReview: React.FC<AddBrandStepReviewProps> = ({
  isDetailsFetched,
  brandName,
  onBrandNameChange,
  logoUrl,
  onLogoError,
}) => {
  return (
    <div className="relative flex items-start gap-4">
      {/* Timeline Connector Line (2 -> 3) */}
      <div className="absolute left-4 top-9 bottom-[-24px] w-[2px] bg-slate-100 overflow-hidden">
        <div
          className={`w-full bg-[#A655F8] transition-all duration-500 ease-out delay-200 ${
            isDetailsFetched ? "h-full" : "h-0"
          }`}
        />
      </div>

      {/* Step Circle */}
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 z-10 transition-all duration-500 delay-100 ${
          isDetailsFetched
            ? "bg-[#A655F8] text-white shadow-sm scale-100"
            : "bg-slate-100 text-slate-400 scale-95"
        }`}
      >
        2
      </div>

      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold text-slate-900">Review Details</h3>
        <p className="text-xs text-slate-500 mt-0.5">
          {isDetailsFetched
            ? "Confirm the details we found for this brand."
            : "Brand details will appear here once you pick a brand."}
        </p>

        <div className="mt-3">
          {!isDetailsFetched ? (
            /* Locked state */
            <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4 flex items-center gap-3.5 transition-all duration-300">
              <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                <Lock className="w-4 h-4 text-slate-400" />
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-700">Waiting for website...</p>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Search for a brand name or website above to continue.
                </p>
              </div>
            </div>
          ) : (
            /* Fetched state with entrance animation */
            <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4 animate-in fade-in-0 slide-in-from-top-3 duration-500 ease-out fill-mode-both">
              {/* Logo Box */}
              <div className="w-14 h-14 bg-white rounded-xl border border-slate-200 shadow-sm flex items-center justify-center overflow-hidden shrink-0 p-1.5 animate-in zoom-in-75 duration-500">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={brandName || "Brand logo"}
                    className="w-full h-full object-contain"
                    onError={onLogoError}
                  />
                ) : (
                  <Building className="w-6 h-6 text-slate-400" />
                )}
              </div>

              {/* Brand Name Input */}
              <div className="flex-1 w-full sm:w-auto min-w-0">
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Brand Name
                </label>
                <Input
                  value={brandName}
                  onChange={(e) => onBrandNameChange(e.target.value)}
                  placeholder="Brand name"
                  className="h-10 rounded-xl bg-white border-slate-200 text-sm font-medium text-slate-900 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[#A655F8] focus:border-[#A655F8] focus:outline-none transition-colors"
                  required
                />
              </div>

              {/* Logo Status Pill */}
              <div className="shrink-0 w-full sm:w-auto">
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Logo
                </label>
                <div className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-medium animate-in fade-in-0 duration-500 delay-150 fill-mode-both">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>Fetched automatically</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
